import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import { PageHeader, Disclaimer } from "@/components/Common";
import { Send, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { CLEARANCE_STATUSES, EXPLICIT_CLEAN, MOOD_TAGS } from "@/lib/constants";
import { GENRES, SUB_GENRES, BEST_USE_OPTIONS, SEARCH_CATEGORIES } from "@/lib/lists";
import { SearchableSelect, SearchWithCategory, matchesSearch } from "@/components/Controlled";
import { formatDurationMMSS } from "@/lib/duration";
import { useAuth, canWrite } from "@/lib/auth";

// MP5 — Ready Tracks: only Client-Ready & Corporate-Ready.
// Master Correction — standardized filters + search-with-category.
// Ready Tracks gating is preserved: backend filter to ready statuses; this page
// does not weaken any compliance/clearance rule.

const ANY = "";

function Indicator({ ok, labelOn = "Ready", labelOff = "Not ready", testid }) {
  return ok ? (
    <span data-testid={`${testid}-on`} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded status-client">
      <CheckCircle2 className="w-3 h-3" />
      {labelOn}
    </span>
  ) : (
    <span data-testid={`${testid}-off`} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded status-incomplete">
      <XCircle className="w-3 h-3" />
      {labelOff}
    </span>
  );
}

function moodCell(tags) {
  if (!tags || tags.length === 0) return <span className="text-xs text-muted-foreground">—</span>;
  const shown = tags.slice(0, 3);
  return (
    <div className="flex flex-wrap gap-1">
      {shown.map((t) => (
        <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-foreground/80">{t}</span>
      ))}
      {tags.length > 3 && <span className="text-[10px] text-muted-foreground">+{tags.length - 3}</span>}
    </div>
  );
}

// Field resolver for the search-with-category control. Keeps the column id
// shared between the URL/category dropdown and the records being scanned.
function readyFieldOf(t, fieldId) {
  switch (fieldId) {
    case "track_title": return t.track_title;
    case "artist":      return t.artist;
    case "writer":      return (t.writers || []).join(", ");
    case "publisher":   return (t.publishers || []).join(", ");
    case "genre":       return t.genre;
    case "sub_genre":   return t.sub_genre;
    case "mood":        return (t.mood_tags || []).join(", ");
    case "catalog_code":return t.catalog_code;
    case "best_use":    return t.best_use;
    case "clearance_status": return t.clearance_status;
    case "bpm":         return t.bpm != null ? String(t.bpm) : "";
    case "duration":    return t.duration != null ? formatDurationMMSS(t.duration) : "";
    case "isrc":        return t.isrc;
    case "notes":       return [t.internal_notes, t.next_action].filter(Boolean).join(" · ");
    default:            return "";
  }
}

export default function ReadyTracks() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const writeAccess = canWrite(user);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // MP4B — live Catalog Code source. Same active-only registry used by
  // Catalog Tracks (/tracks). Previously this page read from a stale,
  // hard-coded CATALOG_ACRONYMS list which is no longer the source of
  // truth — that list has been removed from this page.
  const [catalogCodes, setCatalogCodes] = useState([]);
  const [q, setQ] = useState("");
  const [searchCat, setSearchCat] = useState("all");
  const [clearance, setClearance] = useState(ANY);
  const [genre, setGenre] = useState(ANY);
  const [subGenre, setSubGenre] = useState(ANY);
  const [mood, setMood] = useState(ANY);
  const [catalog, setCatalog] = useState(ANY);
  const [bestUse, setBestUse] = useState(ANY);
  const [explicit, setExplicit] = useState(ANY);

  useEffect(() => {
    api.get("/tracks/ready")
      .then(r => setItems(r.data || []))
      .catch(e => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, []);

  // MP4B — fetch the same live, active-only Catalog Code list that
  // Catalog Tracks uses. Soft-fails so the rest of the page still works
  // if the registry endpoint is temporarily unreachable.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/catalog-codes", { params: { active_only: true } });
        if (!cancelled) setCatalogCodes(Array.isArray(r.data) ? r.data : []);
      } catch (_e) {
        // soft-fail — filter will simply be empty until the registry is reachable
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const catalogOptions = useMemo(
    () => catalogCodes.map((c) => ({ value: c.code, label: `[${c.code}] ${c.name}` })),
    [catalogCodes]
  );

  const anyFieldList = ["track_title", "artist", "writer", "publisher", "genre", "sub_genre", "mood", "catalog_code", "best_use", "clearance_status", "bpm", "duration", "isrc", "notes"];

  const filtered = useMemo(() => {
    return items.filter(t => {
      if (clearance && t.clearance_status !== clearance) return false;
      if (genre && (t.genre || "") !== genre) return false;
      if (subGenre && (t.sub_genre || "") !== subGenre) return false;
      if (catalog && (t.catalog_code || "") !== catalog) return false;
      if (bestUse && (t.best_use || "") !== bestUse) return false;
      if (explicit && (t.explicit_clean_status || "") !== explicit) return false;
      if (mood && !(t.mood_tags || []).includes(mood)) return false;
      if (!matchesSearch(t, q, searchCat, readyFieldOf, anyFieldList)) return false;
      return true;
    });
  }, [items, q, searchCat, clearance, genre, subGenre, mood, catalog, bestUse, explicit]);

  function resetFilters() {
    setQ(""); setSearchCat("all");
    setClearance(ANY); setGenre(ANY); setSubGenre(ANY); setMood(ANY);
    setCatalog(ANY); setBestUse(ANY); setExplicit(ANY);
  }

  return (
    <div data-testid="ready-tracks-page">
      <PageHeader
        title="Ready Tracks"
        subtitle="Only Client-Ready and Corporate-Ready tracks appear here. Eligible for submission packages."
      />

      <Card className="mb-4">
        <CardContent className="p-4 space-y-3">
          <SearchWithCategory
            query={q}
            onQueryChange={setQ}
            category={searchCat}
            onCategoryChange={setSearchCat}
            categories={SEARCH_CATEGORIES.ready}
            placeholder="Search Ready Tracks…"
            testidPrefix="ready"
          />
          <div className="grid lg:grid-cols-7 md:grid-cols-3 sm:grid-cols-2 gap-3 items-end">
            <FilterControl label="Clearance"
              value={clearance} onChange={setClearance}
              options={CLEARANCE_STATUSES.filter(s => ["Client-Ready","Corporate-Ready"].includes(s))}
              testid="ready-filter-clearance" />
            <FilterControl label="Genre"
              value={genre} onChange={setGenre} options={GENRES} testid="ready-filter-genre" />
            <FilterControl label="Sub-genre"
              value={subGenre} onChange={setSubGenre} options={SUB_GENRES} testid="ready-filter-sub-genre" />
            <FilterControl label="Mood"
              value={mood} onChange={setMood} options={MOOD_TAGS} testid="ready-filter-mood" />
            <FilterControl label="Catalog code"
              value={catalog} onChange={setCatalog} options={catalogOptions} testid="ready-filter-catalog" />
            <FilterControl label="Best use"
              value={bestUse} onChange={setBestUse} options={BEST_USE_OPTIONS} testid="ready-filter-bestuse" />
            <FilterControl label="Explicit / Clean"
              value={explicit} onChange={setExplicit} options={EXPLICIT_CLEAN} testid="ready-filter-explicit" />
          </div>
          <div className="flex justify-end gap-3 items-center">
            <span className="text-xs text-muted-foreground" data-testid="ready-count">{filtered.length} of {items.length} ready</span>
            <Button variant="outline" size="sm" onClick={resetFilters} data-testid="ready-filter-reset">Reset filters</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Track ID</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Catalog</TableHead>
                <TableHead>Artist</TableHead>
                <TableHead>Genre</TableHead>
                <TableHead>Mood</TableHead>
                <TableHead>BPM</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Explicit / Clean</TableHead>
                <TableHead>Best use</TableHead>
                <TableHead>Clearance</TableHead>
                <TableHead>Docs</TableHead>
                <TableHead>Submission</TableHead>
                <TableHead>Last reviewed</TableHead>
                <TableHead className="w-[180px] sticky right-0 bg-card"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={15} className="text-center py-10 text-muted-foreground">Loading…</TableCell>
                </TableRow>
              )}
              {!loading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={15} className="text-center py-10 text-muted-foreground">
                    <CheckCircle2 className="w-6 h-6 mx-auto mb-2 opacity-60" />
                    No tracks match these filters. Try resetting filters or promote a track to Client-Ready / Corporate-Ready.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map(t => (
                <TableRow
                  key={t.id}
                  className="cursor-pointer hover:bg-secondary/40"
                  onClick={() => navigate(`/tracks/${t.id}`)}
                  data-testid={`ready-row-${t.id}`}
                >
                  <TableCell className="font-mono text-xs whitespace-nowrap">{t.track_id}</TableCell>
                  <TableCell className="max-w-[180px] truncate font-medium">{t.track_title || "(untitled)"}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {t.catalog_code ? (
                      t.catalog_code
                    ) : (
                      <span className="text-amber-700 text-[11px] italic font-sans" title="Created before MP4 Catalog Code requirement" data-testid={`ready-legacy-${t.id}`}>
                        Unassigned Legacy Record
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="max-w-[140px] truncate">{t.artist || "—"}</TableCell>
                  <TableCell className="text-sm">{t.genre || "—"}</TableCell>
                  <TableCell>{moodCell(t.mood_tags)}</TableCell>
                  <TableCell className="text-sm">{t.bpm || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{formatDurationMMSS(t.duration)}</TableCell>
                  <TableCell className="text-xs">{t.explicit_clean_status || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[220px] truncate">{t.best_use || "—"}</TableCell>
                  <TableCell><StatusBadge status={t.clearance_status} /></TableCell>
                  <TableCell>
                    <Indicator ok={!!t.documents_ready_indicator} labelOn="Complete" labelOff="Missing" testid={`ready-docs-${t.id}`} />
                  </TableCell>
                  <TableCell>
                    <Indicator ok={!!t.submission_ready_indicator} labelOn="Sendable" labelOff="Hold" testid={`ready-sub-${t.id}`} />
                  </TableCell>
                  <TableCell className="font-mono text-[11px] text-muted-foreground whitespace-nowrap">{(t.last_reviewed_date || "").slice(0, 10) || "—"}</TableCell>
                  <TableCell className="sticky right-0 bg-card" onClick={(e) => e.stopPropagation()}>
                    {writeAccess ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/submissions/new?track=${t.id}`)}
                        data-testid={`create-submission-${t.id}`}
                      >
                        <Send className="w-4 h-4 mr-2" />New Submission
                      </Button>
                    ) : (
                      <span className="text-[11px] text-muted-foreground" data-testid={`ready-readonly-${t.id}`}>Read-only</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Disclaimer>
        Compliance support only · Not legal advice · Final clearance may require qualified music/IP counsel.
      </Disclaimer>
    </div>
  );
}

function FilterControl({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-xs text-muted-foreground">{label}</label>
      <div className="mt-1">
        <SearchableSelect
          value={value}
          onChange={onChange}
          options={options}
          placeholder="Any"
          testid={testid}
        />
      </div>
    </div>
  );
}
